import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { getVersionId, useDatabaseContent } from '@databases/database-state';
import { isAIFCStagingAreaView } from '@databases/database-state';
import { isStandalone as isStandaloneCheck } from '@databases/modes';
import { appStoreUtils } from '@databases/redux/store';
import { useApplySystemTemplate } from '@databases/templates/useApplySystemTemplate';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import React, { type FunctionComponent, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { DatabaseError } from './DatabaseError';
import { type DatabaseActionKey } from './common/components/database/DatabaseActions';
import DatabaseContent from './common/components/database-content/DatabaseContent';
import DatabaseContentSkeleton from './common/components/database-content/DatabaseContentSkeleton';

const isStandalone = isStandaloneCheck();

const actions: DatabaseActionKey[] = ['import', 'print', 'help', 'copy', 'feedback'];
if (isStandalone) {
  actions.push('lock');
}

interface DatabaseContentLoaderProps {
  onDataLoaded?: () => void;
}

const DatabaseContentLoader: FunctionComponent<DatabaseContentLoaderProps> = ({
  onDataLoaded,
}) => {
  const { contentId } = useDatabaseContent();
  const versionId = getVersionId();
  const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const { firstLayoutProcessed } = useSnapshot(aiFirstCreationStreamingState);
  const { isStreaming } = useDatabaseStreamState();
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();

  useApplySystemTemplate();

  const databaseStatus = appStoreUtils.useEqualSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  useEffect(() => {
    const logMetrics = async () => {
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      (await getBridgeAPI()).onContentLoadSucceeded?.({});
    };

    if (databaseStatus?.status === AsyncOperationStatus.SUCCEEDED) {
      logMetrics();
      onDataLoaded?.();
    }
  }, [databaseStatus?.status, contentId, onDataLoaded]);

  if (databaseStatus?.status === AsyncOperationStatus.ERROR) {
    return <DatabaseError serializedError={databaseStatus.error} />;
  }

  const isDatabaseLoaded = databaseStatus?.status === AsyncOperationStatus.SUCCEEDED;
  const shouldShowSkeleton =
    !isDatabaseLoaded ||
    (isDbAiFirstCreationEnabled &&
      // firstLayoutProcessed is always false in standalone, so we should only
      // check for databases in AIFC staging area
      isInAIFCStagingAreaView &&
      isStreaming &&
      !firstLayoutProcessed);

  if (shouldShowSkeleton) {
    return <DatabaseContentSkeleton />;
  }

  return (
    <DatabaseContent
      contentId={contentId}
      isContentEditable={!versionId}
      actions={actions}
    />
  );
};

export default DatabaseContentLoader;
